import React from 'react';
import { Box } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { BORDER, TYPE } from '../../design-system/foundation';
import { V3_MOTION } from './motion';
import { phaseForPath, type ExperiencePhase } from './phase';

/* ── Page transition ─────────────────────────────────────────────────── */

export interface PageTransitionProps {
  /**
   * Changing the key replays the fade. Key on the content region (not the full
   * pathname) so section nav and headers rendered inside the route don't
   * re-animate on every sub-tab click.
   */
  routeKey: string;
  children: React.ReactNode;
}

/**
 * Quick (≤150ms) opacity fade when the route content changes. No exit
 * animation and no `mode="wait"`, so the previous screen is never held on
 * screen; skipped entirely when the user prefers reduced motion.
 */
export const PageTransition: React.FC<PageTransitionProps> = ({ routeKey, children }) => {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) {
    return <div style={{ width: '100%' }}>{children}</div>;
  }
  return (
    <motion.div key={routeKey} {...V3_MOTION.pageContent} style={{ width: '100%' }}>
      {children}
    </motion.div>
  );
};

/* ── Stagger reveal ────────────────────────────────────────────────── */

export const StaggerReveal: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <motion.div
    className={className}
    initial="initial"
    animate="animate"
    variants={V3_MOTION.stagger.container}
  >
    {React.Children.map(children, (child) =>
      React.isValidElement(child) ? (
        <motion.div variants={V3_MOTION.stagger.item}>{child}</motion.div>
      ) : (
        child
      ),
    )}
  </motion.div>
);

/* ── Phase atmosphere shell ────────────────────────────────────────── */

export interface ExperienceShellProps {
  phase?: ExperiencePhase;
  pathname?: string;
  children: React.ReactNode;
  /** Hero canvas mode — more vertical space for visualizations */
  canvas?: boolean;
  /** Solid background — no phase gradients or glow orb */
  flat?: boolean;
}

export const ExperienceShell: React.FC<ExperienceShellProps> = ({
  phase: phaseProp,
  pathname = '',
  children,
  canvas = false,
  flat = false,
}) => {
  const phase = phaseProp ?? phaseForPath(pathname);

  return (
    <Box
      className="v3-experience-shell"
      data-phase={phase ?? 'neutral'}
      data-flat={flat ? 'true' : undefined}
      sx={{
        position: 'relative',
        width: flat ? '100%' : undefined,
        maxWidth: flat ? 'none' : undefined,
        minHeight: canvas ? 'min(72vh, 720px)' : undefined,
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>{children}</Box>
    </Box>
  );
};

/* ── Hero canvas — primary visualization zone ──────────────────────── */

export interface HeroCanvasProps {
  phase?: ExperiencePhase;
  title?: string;
  children: React.ReactNode;
  toolbar?: React.ReactNode;
  minHeight?: number;
}

export const HeroCanvas: React.FC<HeroCanvasProps> = ({
  title,
  children,
  toolbar,
  minHeight = 520,
}) => {
  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="v3-hero-canvas"
      sx={{
        position: 'relative',
        borderRadius: '12px',
        overflow: 'hidden',
        minHeight,
        bgcolor: 'var(--panel-bg)',
        boxShadow: 'var(--panel-shadow)',
        border: '1px solid var(--border-default)',
      }}
    >
      {(title || toolbar) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderBottom: `1px solid ${BORDER.subtle}`,
          }}
        >
          {title && (
            <Box
              component="span"
              sx={{ ...TYPE.subheading, color: 'text.primary' }}
            >
              {title}
            </Box>
          )}
          {toolbar}
        </Box>
      )}
      <Box sx={{ position: 'relative', height: '100%' }}>{children}</Box>
    </Box>
  );
};

/* ── Animated metric ─────────────────────────────────────────────────── */

export const MetricReveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({
  children,
  delay = 0,
}) => (
  <motion.div
    initial={V3_MOTION.metric.initial}
    animate={V3_MOTION.metric.animate}
    transition={{ ...V3_MOTION.metric.transition, delay }}
  >
    {children}
  </motion.div>
);
